import DepositVault from '@components/deposit-vault';
import { useVaultCoin } from '@hooks/use-coins';
import { formatAddress } from '@mysten/sui/utils';
import { numericFormat } from '@utils/index';
import { notification, Segmented, Tabs } from 'antd';
import { FC, useState } from 'react';
import { OpenapiClientParrotVault } from 'src/openapi/parrot-vault';
import styles from './index.module.scss';
import copyIcon from '@assets/images/vault/copy.svg';
import copyHoverIcon from '@assets/images/vault/copy-hover.svg';
import explorerIcon from '@assets/images/header/explorer.svg';
import explorerHoverIcon from '@assets/images/header/explorer-hover.svg';
import drawDownIcon from '@assets/images/vault/draw-down.svg';
import { useCopyToClipboard, useWindowSize } from 'react-use';
import { ClmmPoolUtil, TickMath } from '@cetusprotocol/cetus-sui-clmm-sdk';
import { BN, Decimal } from 'parrot-sui-sdk';
import { usePool } from '@hooks/use-pool';
import { useMyVault } from '@hooks/use-my-vaults';
import WithdrawVault from '@components/withdraw-vault';
import LiquiditySlippage from '@components/liquidity-slippage';
import { scaleDown } from '@utils/math';
import { Link } from 'react-router-dom';
import { globalSettingModel } from '@models/global-setting.model';
import { CoinStruct } from '@models/coins.vault.model';
import More from './more';

const tabsItem = ['Deposit', 'Withdraw'];

interface OwnProps {
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
  close(): void;
  show: boolean;
  toggleShow(value?: boolean): void;
}

const DialogContent: FC<OwnProps> = ({ vault, close, show, toggleShow }) => {
  const { width } = useWindowSize();
  const { pool } = usePool(vault.pool_id);
  const { myVault } = useMyVault(vault.vault_id);
  const { coin: coinA } = useVaultCoin(vault.coin_type_a);
  const { coin: coinB } = useVaultCoin(vault.coin_type_b);

  const [tabValue, setTabValue] = useState(tabsItem[0]);

  if (!coinA || !coinB) {
    return null;
  }

  let ratioA = 0;
  let ratioB = 0;
  let percent: number = 0;
  let amountA = '0';
  let amountB = '0';
  let amountA_USD = '0';
  let amountB_USD = '0';

  if (pool) {
    const ratio = ClmmPoolUtil.calculateDepositRatioFixTokenA(
      vault.tick_lower,
      vault.tick_upper,
      new BN(pool.current_sqrt_price),
    );
    ratioA = Number(ratio.ratioA.mul(100).toFixed(2));
    ratioB = new Decimal(100).sub(ratioA).toNumber();

    const _price_current = TickMath.sqrtPriceX64ToPrice(
      new BN(pool.current_sqrt_price),
      coinA.decimals,
      coinB.decimals,
    );

    percent = _price_current
      .sub(vault.price_lower)
      .div(new Decimal(vault.price_upper).sub(vault.price_lower))
      .mul(100)
      .toNumber();

    const coinsAmount = ClmmPoolUtil.getCoinAmountFromLiquidity(
      new BN(vault.liquidity),
      new BN(pool.current_sqrt_price),
      TickMath.tickIndexToSqrtPriceX64(vault.tick_lower),
      TickMath.tickIndexToSqrtPriceX64(vault.tick_upper),
      false,
    );

    amountA = scaleDown(coinsAmount.coinA.toString(), coinA.decimals).toString();
    amountB = scaleDown(coinsAmount.coinB.toString(), coinB.decimals).toString();

    amountA_USD = new Decimal(amountA).mul(coinA.price_usd).toString();
    amountB_USD = new Decimal(amountB).mul(coinB.price_usd).toString();
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.draw_hide} onClick={() => close()}>
        <img src={drawDownIcon} data-icon />
      </div>
      <div className={styles.top}>
        <div className={styles.symbol}>
          <div className={styles.symbol_img}>
            <img src={coinA.icon} />
            <img src={coinB.icon} />
          </div>
          <span>
            {coinA.symbol} - {coinB.symbol}
          </span>
        </div>
        <div className={styles.apy}>
          Deposit APY<span>{numericFormat(vault.apy, { decimalScale: 2 })}%</span>
        </div>

        {show && width > 640 && <VaultAddress vault={vault} />}
      </div>
      <div className={styles.main}>
        <div className={styles.left}>
          <div className={styles.tabs}>
            <Segmented
              value={tabValue}
              options={tabsItem}
              onChange={(value) => {
                setTabValue(value); // string
              }}
            />
            <LiquiditySlippage />
          </div>

          {tabValue === 'Deposit' && (
            <DepositVault
              myVault={myVault}
              vault={vault}
              pool={pool}
              coinA={coinA}
              coinB={coinB}
              closeDialog={close}
            />
          )}

          {tabValue === 'Withdraw' && (
            <WithdrawVault
              myVault={myVault}
              vault={vault}
              pool={pool}
              coinA={coinA}
              coinB={coinB}
              closeDialog={close}
            />
          )}
        </div>

        {show && width > 640 && (
          <div className={styles.right}>
            <div className={styles.right_content}>
              <div className={styles.right_title}>Position Range</div>
              <Range vault={vault} percent={percent} />

              <div className={styles.line}></div>
              <div className={styles.right_title}>Vault Composition</div>
              <Composition
                coinA={coinA}
                coinB={coinB}
                ratioA={ratioA}
                ratioB={ratioB}
                amountA={amountA}
                amountB={amountB}
                amountA_USD={amountA_USD}
                amountB_USD={amountB_USD}
              />

              <div className={styles.line}></div>
              <Fee />
            </div>
          </div>
        )}

        <div className={styles.mobile_right}>
          {show && (
            <Tabs
              items={[
                {
                  label: 'Range',
                  key: '1',
                  children: <Range vault={vault} percent={percent} />,
                },
                {
                  label: 'Composition',
                  key: '2',
                  children: (
                    <Composition
                      coinA={coinA}
                      coinB={coinB}
                      ratioA={ratioA}
                      ratioB={ratioB}
                      amountA={amountA}
                      amountB={amountB}
                      amountA_USD={amountA_USD}
                      amountB_USD={amountB_USD}
                    />
                  ),
                },
                {
                  label: 'Fees',
                  key: '3',
                  children: (
                    <>
                      <Fee />{' '}
                      <div className={styles.mar16}>
                        <VaultAddress vault={vault} />
                      </div>
                    </>
                  ),
                },
              ]}
            />
          )}

          <More show={show} toggleShow={toggleShow} />
        </div>
      </div>
    </div>
  );
};

const Range: FC<{
  percent: number;
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
}> = ({ percent, vault }) => {
  return (
    <>
      <div className={styles.vault_range}>
        <div className={styles.vault_range_bg}>
          <div
            className={styles.current_price}
            style={{
              left: percent + '%',
              transform: `translate(${percent < 10 ? 0 : '-50%'}, 0)`,
            }}
          >
            <div className={styles.current_price_label}>Current Price</div>
            <div className={styles.current_price_value}>{numericFormat(vault.price_current)}</div>
          </div>
          <div className={styles.vault_range_line} style={{ left: percent + '%' }}></div>
        </div>
      </div>
      <div className={styles.vault_price}>
        <div>
          <div className={styles.vault_price_label}>Mini Price</div>
          <div className={styles.vault_price_value}>{numericFormat(vault.price_lower)}</div>
        </div>
        <div>
          <div className={styles.vault_price_label}>Mini Price</div>
          <div className={styles.vault_price_value}>{numericFormat(vault.price_upper)}</div>
        </div>
      </div>
    </>
  );
};

const Composition: FC<{
  coinA: CoinStruct;
  coinB: CoinStruct;
  ratioA: number;
  ratioB: number;
  amountA: string;
  amountB: string;
  amountA_USD: string;
  amountB_USD: string;
}> = ({ coinA, coinB, ratioA, ratioB, amountA, amountB, amountA_USD, amountB_USD }) => {
  return (
    <>
      <div className={styles.symbol_icon}>
        <img src={coinA.icon} />
        <img src={coinB.icon} />
      </div>
      <div className={styles.ratio}>
        <div className={styles.ratio_value} style={{ width: `${ratioA}%` }}></div>
      </div>

      <div className={styles.symbol_data}>
        <div>
          <div className={styles.vault_symbol}>
            {coinA.symbol}
            <span>({ratioA}%)</span>
          </div>
          <div className={styles.vault_symbol_num}>{numericFormat(amountA)}</div>
          <div className={styles.vault_symbol_usd}>${numericFormat(amountA_USD)}</div>
        </div>
        <div className={styles.symbol_data_right}>
          <div className={styles.vault_symbol}>
            {coinB.symbol}
            <span>({ratioB}%)</span>
          </div>
          <div className={styles.vault_symbol_num}>{numericFormat(amountB)}</div>
          <div className={styles.vault_symbol_usd}>${numericFormat(amountB_USD)}</div>
        </div>
      </div>
    </>
  );
};

const Fee: FC = () => {
  return (
    <>
      <div className={styles.withdrawal_fee}>
        <div>Withdrawal Fees</div>
        <div>Zero</div>
      </div>
      <div className={styles.performance_fee}>
        <div>Performance Fee</div>
        <div>20% of the Yield</div>
      </div>
    </>
  );
};

const VaultAddress: FC<{
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
}> = ({ vault }) => {
  const [, copy] = useCopyToClipboard();
  return (
    <div className={styles.vault_address}>
      <span> Vault Address</span>
      <span>{formatAddress(vault.vault_id)}</span>
      <div
        className={styles.copy}
        onClick={() => {
          copy(vault.vault_id);
          notification.open({
            message: 'Vault address is copied',
          });
        }}
      >
        <img src={copyIcon} data-icon />
        <img src={copyHoverIcon} data-icon className={styles.hover} />
      </div>
      <Link
        className={styles.copy}
        to={globalSettingModel.explorerUrlForObject(vault.vault_id)}
        target="_blank"
      >
        <img src={explorerIcon} data-icon />
        <img src={explorerHoverIcon} data-icon className={styles.hover} />
      </Link>
    </div>
  );
};

export default DialogContent;
